// THE NAVIGATOR COMES BACK ON A SCROLL UP (law_stage, 2026-10-12, John).
//
// "When scrolling up, even without reaching the top, a small intentional amount
// of upscroll should unfold the expanded view of the navigator, so anybody can
// reach the top areas whenever they want."
//
// A folded navigator used to open only at the very top of the page. Now a
// deliberate movement up (a flick, a few wheel notches) opens it wherever you
// are, and heading back down folds it again. Intentional means a RUN: the
// upward distance has to add up without a downward step in between, so the
// small back-and-forth of a trackpad settling, or a momentum tail, never
// flashes the navigator open. It commits, like the fold itself: open or folded,
// never half (John, 2026-09-23: "states where it is not fully one or the other").

/** px of unbroken upward scroll that opens a folded navigator */
export const REVEAL_UP_PX = 56;
/** px of downward scroll that folds a revealed navigator again */
export const REHIDE_DOWN_PX = 24;
/** a step smaller than this is noise, in either direction */
const JITTER_PX = 1;

/**
 * Feed it every scroll position; it says whether a navigator the scroll has
 * folded should stand open anyway (revealed by a scroll up). `folded` is what
 * the position alone says (past the fold line); at the top there is nothing to
 * reveal and the run starts over. `shift` is how far the stage itself moved
 * the page since the last call (lib/stage/scroller nudgeScroll): not the
 * reader's movement, so it counts for nothing.
 */
export function revealTracker(): (y: number, folded: boolean, shift?: number) => boolean {
  let last: number | null = null;
  let up = 0;
  let down = 0;
  let revealed = false;
  return (y, folded, shift = 0) => {
    if (last === null) {
      last = y;
      return false;
    }
    last += shift;
    const dy = y - last;
    last = y;
    if (dy < -JITTER_PX) {
      up += -dy;
      down = 0;
    } else if (dy > JITTER_PX) {
      down += dy;
      up = 0;
    }
    if (!folded) {
      revealed = false;
      up = 0;
      return false;
    }
    if (!revealed && up >= REVEAL_UP_PX) {
      revealed = true;
      down = 0;
    } else if (revealed && down >= REHIDE_DOWN_PX) {
      revealed = false;
      up = 0;
    }
    return revealed;
  };
}
